import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  SignaturePayload,
  SignatureRequestView,
  SignPayload,
} from "../features/signatures/signature.types";

/** E-signatures, firm side (backend api/signatures). */
export const signaturesSlice = createApi({
  reducerPath: "signaturesSlice",
  tagTypes: ["Signatures"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getClientSignatures: build.query<SignatureRequestView[], string>({
      query: (customerId) => `api/signatures/client/${customerId}`,
      providesTags: ["Signatures"],
    }),
    getSignature: build.query<SignatureRequestView, string>({
      query: (id) => `api/signatures/${id}`,
      providesTags: ["Signatures"],
    }),
    createSignature: build.mutation<SignatureRequestView, SignaturePayload>({
      query: (body) => ({ url: "api/signatures", method: "POST", body }),
      invalidatesTags: ["Signatures"],
    }),
    remindSignature: build.mutation<boolean, string>({
      query: (id) => ({ url: `api/signatures/${id}/remind`, method: "POST" }),
      invalidatesTags: ["Signatures"],
    }),
    cancelSignature: build.mutation<boolean, string>({
      query: (id) => ({ url: `api/signatures/${id}/cancel`, method: "POST" }),
      invalidatesTags: ["Signatures"],
    }),
    getFirmSigningView: build.query<SignatureRequestView, string>({
      query: (id) => `api/signatures/${id}/sign`,
    }),
    firmSign: build.mutation<
      SignatureRequestView,
      { id: string; body: SignPayload }
    >({
      query: ({ id, body }) => ({
        url: `api/signatures/${id}/sign`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Signatures"],
    }),
    firmDecline: build.mutation<boolean, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `api/signatures/${id}/decline`,
        method: "POST",
        body: { reason: reason || null },
      }),
      invalidatesTags: ["Signatures"],
    }),
  }),
});

/** PDF bytes for the viewer (firm sign-in). */
export const fetchFirmPdf = (path: string) =>
  fetchPdf(apiUrl(path), getAuthHeader().Authorization);

export const apiUrl = (path: string) =>
  `${devConfig.getServerBaseUrlForEnv().replace(/\/+$/, "")}/${path.replace(
    /^\/+/,
    "",
  )}`;

export const fetchPdf = async (url: string, authorization?: string) => {
  const res = await fetch(url, {
    headers: authorization ? { Authorization: authorization } : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(
      text && !text.trim().startsWith("<") && text.length < 300
        ? text
        : "The document couldn't be opened.",
    ) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  return res.arrayBuffer();
};

export const {
  useGetClientSignaturesQuery,
  useGetSignatureQuery,
  useCreateSignatureMutation,
  useRemindSignatureMutation,
  useCancelSignatureMutation,
  useGetFirmSigningViewQuery,
  useFirmSignMutation,
  useFirmDeclineMutation,
} = signaturesSlice;
